import moment from 'moment-timezone'
import { dayjs } from '@retailer-platform/shared-components'

/**
 * Returns the timezone for the current user
 * @date 9/28/2022 - 12:23:30 PM
 *
 * @returns {string}
 */
export const currentUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Returns the current datetime
 * @date 9/28/2022 - 12:23:30 PM
 *
 * @param {boolean} localize whether or not we should localize to current user timezone
 * @returns {moment.Moment} current date time
 */
export const currentTime = (localize: boolean): moment.Moment => {
  if (localize) return moment().tz(currentUserTimezone())
  return moment()
}

/**
 * Creates an ISO 8601 datetime string for a given date
 * @date 2/1/2023 - 7:55:31 AM
 *
 * @param {Date} date
 * @param {?string} [timezone]
 * @returns {(string | null)}
 */
export const dateToMidnightISOString = (date: string, timezone?: string): string | null => {
  if (!date) return null
  return moment
    .tz(date, timezone || currentUserTimezone())
    .startOf('day')
    .toISOString()
}

/**
 * Converts a date and time string to an ISO string. Converts to midnight ISO string if time is not present
 * @date 1/20/2026 - 13:00:05
 *
 * @param {string} date - The date string to convert
 * @param {?string} time - The time string to convert
 * @param {?string} [timezone] - The timezone to use
 * @returns {string | null} The ISO string
 */
export const dateTimeToISOString = (
  date: string,
  time?: string,
  timezone?: string
): string | null => {
  if (!date) return null
  if (!time) return dateToMidnightISOString(date, timezone)
  return moment.tz(`${date} ${time}`, timezone || currentUserTimezone()).toISOString()
}

/**
 * Converts the time part of the ISO 8601 time string to HH:mm format (24 hour). Used to pass the time to input = "time" fields.
 *
 * @param time - The time string to convert
 * @returns { string } The time string in HH:mm format
 */
export const timeStringToHoursMinutes = (time: string) => {
  const timeParts = time.split(':')

  return timeParts[0] + ':' + timeParts[1] // HH:mm
}

/**
 * Formats a date string into MM/DD/YYYY format
 * @date 11/14/2023
 *
 * @param {string} date - The date string to format
 * @returns {string} The formatted date string in MM/DD/YYYY format
 */
export const dateToMMDDYYYY = (date: string) => moment(date).format('MM/DD/YYYY')

/**
 * Formats a date string into a relative time string (e.g. "2 hours ago")
 * @date 11/14/2023
 *
 * @param {string} date - The date string to convert to relative time
 * @returns {string} A human readable string describing how long ago the date was
 */
export const timeSince = (date: string) => moment(date).fromNow()

/**
 * Formats a date and optional time string for display in the user's browser locale
 * @date 1/20/2026
 *
 * @param {string} date - The date string (YYYY-MM-DD format)
 * @param {string} [time] - Optional time string (HH:mm format)
 * @returns {string} Formatted date/time string in the user's locale
 */
export const formatDateTimeForDisplay = (date: string, time?: string): string => {
  if (!date) return ''

  // If no time is provided, just format the date according to user's locale
  if (!time) {
    return moment(date).toDate().toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  // Combine date and time, then format for user's locale
  const dateTime = moment(`${date} ${time}`)

  // Use toLocaleString to format according to user's browser locale
  return dateTime.toDate().toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Validates if a string is in valid HH:MM format
 * @param {string} timeString - The time string to validate
 * @returns {boolean} True if the string is in valid HH:MM format, false otherwise
 */
export const isValidTimeString = (timeString: string): boolean => {
  if (!timeString || typeof timeString !== 'string') {
    return false
  }

  // Use dayjs to validate with strict mode
  return dayjs(timeString, 'HH:mm', true).isValid()
}

/**
 * Formats hour and minute numbers into HH:MM string format
 * Returns empty string if the values are invalid
 * @param {number | undefined} hour - The hour (0-23)
 * @param {number | undefined} minute - The minute (0-59)
 * @returns {string} The formatted time string in HH:MM format, or empty string if invalid
 */
export const formatTimeString = (hour?: number, minute?: number): string => {
  // Validate ranges
  if (
    hour === undefined ||
    minute === undefined ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return ''
  }

  // Use dayjs to format
  const time = dayjs().hour(hour).minute(minute)
  return time.format('HH:mm')
}

/**
 * Parses a HH:MM time string into hour and minute components
 * Returns null if the time string is invalid
 * @param {string} timeString - The time string to parse (e.g., "09:30")
 * @returns {{ hours: number; minutes: number } | null} The parsed hours and minutes, or null if invalid
 */
export const parseTimeString = (timeString: string): { hours: number; minutes: number } | null => {
  const parsed = dayjs(timeString, 'HH:mm', true)

  if (!parsed.isValid()) {
    return null
  }

  return {
    hours: parsed.hour(),
    minutes: parsed.minute(),
  }
}
